import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid } from 'date-fns';
import { Box, capitalizeFirstLetter, GetColorStatus, MaterialSymbol, withStyles } from 'vopay';
import { Title } from 'UI';
import styles from './ElinxStatusStepper.module.scss';
import { ElinxItem } from './useElinx';

const cn = withStyles(styles);

export type ElinxStatusStepperProps = {
  status: ElinxItem['Status'];
  date?: string;
};

const STATUS_LABEL_KEYS: Partial<Record<ElinxItem['Status'], string>> = {
  pending: 'pending',
  completed: 'completed',
  cancelled: 'cancelled',
  declined: 'declined',
  expired: 'expired',
};

// Memoized — the sole caller (ElinxDetails) re-renders on unrelated async state (funding-parent
// fetch, transaction/document fetches) that never changes status/date, so this would otherwise
// re-render every time despite taking only these two primitive props.
function ElinxStatusStepper({ status, date }: ElinxStatusStepperProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory.Details.StatusStepper' });

  const timestamp = date && isValid(new Date(date)) ? format(new Date(date), 'dd MMM yyyy, HH:mm:ss') : '';

  if (status !== 'waiting' && status !== 'pending approval') {
    const colorStatus = GetColorStatus(status);
    const colorClass = colorStatus === 'positive' ? 'green' : colorStatus === 'negative' ? 'red' : undefined;
    const label = t(STATUS_LABEL_KEYS[status] ?? status, { defaultValue: capitalizeFirstLetter(status) });

    return (
      <div className={cn('step')}>
        <div className={cn('stepIcon', colorClass)}>
          {colorClass === 'green' && <MaterialSymbol icon="check" />}
          {colorClass === 'red' && <MaterialSymbol icon="close" />}
        </div>
        <Title level="4" className={cn(colorClass ?? 'stepLabelCurrent')}>
          {label}
        </Title>
      </div>
    );
  }

  const currentLabel =
    status === 'waiting'
      ? t('waiting', { defaultValue: 'Waiting' })
      : t('pendingApproval', { defaultValue: 'Pending Approval' });
  const currentMessage =
    status === 'waiting'
      ? t('waitingMessage', {
          defaultValue: 'This request will stay in waiting until the funding transaction is settled.',
        })
      : t('pendingApprovalMessage', { defaultValue: 'This request is awaiting approval.' });

  const upcomingLabel =
    status === 'waiting'
      ? t('pending', { defaultValue: 'Pending' })
      : t('waiting', { defaultValue: 'Waiting' });

  return (
    <div className={cn('stepper')}>
      <div className={cn('step', 'border')}>
        <div className={cn('stepIcon')} />
        <Box flx="col" gapSize="4px">
          <Title level="4" className={cn('stepLabelCurrent')}>
            {currentLabel}
          </Title>
          <div className={cn('stepMessage')}>{currentMessage}</div>
          {timestamp && <div className={cn('stepTimestamp')}>{timestamp}</div>}
        </Box>
      </div>
      <div className={cn('step')}>
        <div className={cn('stepIcon', 'small')} />
        <Title level="5" className={cn('stepLabelUpcoming')}>
          {upcomingLabel}
        </Title>
      </div>
    </div>
  );
}

export default memo(ElinxStatusStepper);
